import * as React from 'react';
import { cn } from '@/lib/utils';
import { FieldErrorIcon, fieldErrorMessageClassName, invalidFieldClassName } from './field-error';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  label?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, label, id, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = id || props.name || generatedId;
    const invalid = !!error || props['aria-invalid'] === true || props['aria-invalid'] === 'true';
    const errorId = `${inputId}-error`;
    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-2">
            {label}
          </label>
        )}
        <div className="relative">
          <input
            id={inputId}
            type={type}
            className={cn(
              'flex h-9 w-full rounded-md border border-gray-300 bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50',
              className,
              invalid && cn(invalidFieldClassName, 'pr-9'),
            )}
            ref={ref}
            {...props}
            aria-invalid={invalid}
            aria-describedby={
              [props['aria-describedby'], error && errorId].filter(Boolean).join(' ') || undefined
            }
          />
          {invalid && <FieldErrorIcon className="absolute right-3 top-1/2 -translate-y-1/2" />}
        </div>
        {error && (
          <p id={errorId} className={cn('mt-1', fieldErrorMessageClassName)}>
            {error}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
